import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import apiClient, { getErrorMessage } from '../../utils/axios';
import { formatKstDateTime, formatNumber } from '../../utils/formatUtils';
import { PageHeader, useNotify, useConfirm } from '../../components/common';

const VERSION_RE = /^\d+(\.\d+){1,3}$/;
const SHA256_RE = /^[0-9a-fA-F]{64}$/;

const EMPTY_FORM = { version: '', downloadUrl: '', sha256: '', notes: '', isActive: true };

const ReleaseDialog = ({ open, release, onClose, onSaved }) => {
  const notify = useNotify();
  const isEdit = !!release;
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [hashing, setHashing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(
      release
        ? {
            version: release.version,
            downloadUrl: release.downloadUrl,
            sha256: release.sha256,
            notes: release.notes || '',
            isActive: release.isActive,
          }
        : EMPTY_FORM
    );
  }, [open, release]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const versionError = !isEdit && form.version && !VERSION_RE.test(form.version.trim());
  const urlError = form.downloadUrl && !/^https:\/\/\S+$/i.test(form.downloadUrl.trim());
  const shaError = form.sha256 && !SHA256_RE.test(form.sha256.trim());
  const canSave =
    !saving &&
    !hashing &&
    VERSION_RE.test(form.version.trim()) &&
    /^https:\/\/\S+$/i.test(form.downloadUrl.trim()) &&
    SHA256_RE.test(form.sha256.trim());

  const computeSha = async () => {
    setHashing(true);
    setError('');
    try {
      const res = await apiClient.post(
        '/admin/agent-releases/compute-sha256',
        { downloadUrl: form.downloadUrl.trim() },
        { timeout: 6 * 60 * 1000 }
      );
      setForm((f) => ({ ...f, sha256: res.data.sha256 }));
      notify(`sha256 을 계산했습니다. (${formatNumber(res.data.size)} bytes)`);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setHashing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const body = {
      downloadUrl: form.downloadUrl.trim(),
      sha256: form.sha256.trim().toLowerCase(),
      notes: form.notes.trim(),
      isActive: form.isActive,
    };
    try {
      const res = isEdit
        ? await apiClient.patch(`/admin/agent-releases/${release.id}`, body)
        : await apiClient.post('/admin/agent-releases', { ...body, version: form.version.trim() });
      notify(res.data.message);
      onSaved();
      onClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving || hashing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SystemUpdateAltIcon color="primary" />
        {isEdit ? `배포 버전 수정 — v${release.version}` : '새 배포 버전 등록'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="버전"
            placeholder="1.0.3"
            value={form.version}
            onChange={set('version')}
            disabled={isEdit}
            error={!!versionError}
            helperText={isEdit ? '버전은 바꿀 수 없습니다.' : '에이전트 package.json 의 version 과 같게 입력하세요.'}
          />
          <TextField
            label="다운로드 주소"
            placeholder="https://plango.today/downloads/plango-agent-1.0.3.tar.gz"
            value={form.downloadUrl}
            onChange={set('downloadUrl')}
            error={!!urlError}
            helperText={urlError ? 'https:// 로 시작해야 합니다.' : 'scripts/build-release.sh 로 만든 tar.gz 주소'}
          />
          <TextField
            label="sha256"
            value={form.sha256}
            onChange={set('sha256')}
            error={!!shaError}
            helperText={shaError ? '16진수 64자리여야 합니다.' : '서버에서 sha256sum 결과를 붙여 넣거나 자동 계산을 누르세요.'}
            InputProps={{
              sx: { fontFamily: 'monospace', fontSize: 13 },
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={computeSha}
                    disabled={hashing || !/^https:\/\/\S+$/i.test(form.downloadUrl.trim())}
                    startIcon={hashing ? <CircularProgress size={14} /> : null}
                  >
                    {hashing ? '계산 중' : '자동 계산'}
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TextField label="변경 내용 (메모)" value={form.notes} onChange={set('notes')} multiline minRows={2} />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />}
            label="배포 사용 (켜진 버전 중 가장 높은 버전이 에이전트에 배포됩니다)"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving || hashing}>
          취소
        </Button>
        <Button variant="contained" onClick={save} disabled={!canSave}>
          {isEdit ? '저장' : '등록'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AgentReleases = () => {
  const notify = useNotify();
  const [confirmDialog, ask] = useConfirm();
  const [releases, setReleases] = useState([]);
  const [latestId, setLatestId] = useState(null);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, release: null });

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/agent-releases');
      setReleases(res.data.releases);
      setLatestId(res.data.latestId);
      setError('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (r) => {
    try {
      const res = await apiClient.patch(`/admin/agent-releases/${r.id}`, { isActive: !r.isActive });
      notify(res.data.message);
      load();
    } catch (e) {
      notify(getErrorMessage(e), 'error');
    }
  };

  const remove = (r) =>
    ask({
      title: '배포 버전 삭제',
      message: `v${r.version} 을 삭제할까요?\n이미 내려받은 에이전트에는 영향이 없습니다.`,
      confirmText: '삭제',
      danger: true,
      icon: <DeleteOutlineIcon color="error" />,
      onConfirm: async () => {
        try {
          const res = await apiClient.delete(`/admin/agent-releases/${r.id}`);
          notify(res.data.message);
          load();
        } catch (e) {
          notify(getErrorMessage(e), 'error');
        }
      },
    });

  return (
    <>
      <PageHeader
        title="에이전트 배포"
        description="배포 사용이 켜진 버전 중 가장 높은 버전을 에이전트가 로그인 때 내려받고, 로그아웃 직후 또는 07:55 에 교체합니다."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, release: null })}>
            새 버전 등록
          </Button>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>버전</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>다운로드 주소</TableCell>
              <TableCell>sha256</TableCell>
              <TableCell>메모</TableCell>
              <TableCell>등록일시</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {releases.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  등록된 배포 버전이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {releases.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>v{r.version}</TableCell>
                <TableCell>
                  {r.id === latestId ? (
                    <Chip size="small" label="배포중" color="success" />
                  ) : (
                    <Chip
                      size="small"
                      label={r.isActive ? '대기' : '중지'}
                      color={r.isActive ? 'info' : 'default'}
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Tooltip title={r.downloadUrl}>
                    <Typography variant="body2" noWrap>
                      {r.downloadUrl}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={r.sha256}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {r.sha256.slice(0, 12)}…
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-line' }}>{r.notes || '-'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatKstDateTime(r.createdAt, { seconds: false })}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => toggleActive(r)}>
                    {r.isActive ? '중지' : '사용'}
                  </Button>
                  <Button size="small" onClick={() => setDialog({ open: true, release: r })}>
                    수정
                  </Button>
                  <Button size="small" color="error" onClick={() => remove(r)}>
                    삭제
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <ReleaseDialog
        open={dialog.open}
        release={dialog.release}
        onClose={() => setDialog({ open: false, release: null })}
        onSaved={load}
      />
      {confirmDialog}
    </>
  );
};

export default AgentReleases;
